import "./browserInfo";
import { untrack, createEffect, onCleanup, on, createUniqueId, createMemo, createComputed, } from "solid-js";
import { hasDisplayNone, queryElement } from "./utils";
import { dismissStack, addDismissStack, removeDismissStack, } from "./global/dismissStack";
import { addGlobalEvents, globalState, onDocumentClick, removeGlobalEvents, } from "./global/globalEvents";
import { onClickDocument, onFocusFromOutsideAppOrTab, removeOutsideFocusEvents, } from "./local/outside";
import { addMenuPopupEl, removeMenuPopupEl } from "./local/menuPopup";
import { getMenuButton, markFocusedMenuButton, onBlurMenuButton, onClickMenuButton, onClickOutsideMenuButton, onFocusMenuButton, onKeydownMenuButton, onMouseDownMenuButton, removeMenuButtonEvents, } from "./local/menuButton";
import CreatePortal from "./components/CreatePortal";
import { Transition } from "./components/Transition";
import { removeLocalEvents } from "./local/manageLocalEvents";
import { onFocusInContainer, onFocusOutContainer, runFocusOnActive, } from "./local/container";
import { onClickOverlay } from "./local/overlay";
import { activateLastFocusSentinel, onFocusSentinel, } from "./local/focusSentinel";
/**
 *
 * Handles "click outside" behavior for button popup pairings. Closing is triggered by click/focus outside of popup element or pressing "Escape" key.
 */
const Dismiss = (props) => {
    const { id = "", menuButton, menuPopup, focusElementOnClose, focusElementOnOpen, cursorKeys = false, closeWhenMenuButtonIsTabbed = false, closeWhenMenuButtonIsClicked = true, closeWhenScrolling = false, closeWhenDocumentBlurs = false, closeWhenOverlayClicked = true, closeWhenEscapeKeyIsPressed = true, overlay = false, overlayElement = false, trapFocus = false, removeScrollbar = false, enableLastFocusSentinel = false, mount, 
    // stopComponentEventPropagation = false,
    show = false, onOpen, } = props;
    const state = {
        mount,
        addedFocusOutAppEvents: false,
        closeWhenOverlayClicked,
        closeWhenDocumentBlurs,
        closeWhenEscapeKeyIsPressed,
        closeWhenMenuButtonIsClicked,
        closeWhenMenuButtonIsTabbed,
        closeWhenScrolling,
        cursorKeys,
        focusElementOnClose,
        focusElementOnOpen,
        id,
        uniqueId: createUniqueId(),
        menuBtnId: "",
        focusedMenuBtn: { el: null },
        menuBtnKeyupTabFired: false,
        menuButton,
        timeouts: {
            containerFocusTimeoutId: null,
            menuButtonBlurTimeoutId: null,
        },
        upperStackRemovedByFocusOut: false,
        menuPopup,
        closeByDismissEvent: false,
        menuPopupAdded: false,
        enableLastFocusSentinel,
        overlay,
        overlayElement,
        removeScrollbar,
        trapFocus,
        hasFocusSentinels: !!focusElementOnClose ||
            overlay ||
            !!overlayElement ||
            trapFocus ||
            enableLastFocusSentinel,
        open: props.open,
        setOpen: props.setOpen,
        onClickOutsideMenuButtonRef: () => onClickOutsideMenuButton(state),
        onClickDocumentRef: (e) => onClickDocument(state, e),
        onClickOverlayRef: () => onClickOverlay(state),
        onFocusInContainerRef: (e) => onFocusInContainer(state, e),
        onFocusOutContainerRef: (e) => onFocusOutContainer(state, e),
        onBlurMenuButtonRef: (e) => onBlurMenuButton(state, e),
        onClickMenuButtonRef: (e) => onClickMenuButton(state, e),
        onMouseDownMenuButtonRef: (e) => onMouseDownMenuButton(state, e),
        onFocusFromOutsideAppOrTabRef: (e) => onFocusFromOutsideAppOrTab(state, e),
        onFocusMenuButtonRef: () => onFocusMenuButton(state),
        onKeydownMenuButtonRef: (e) => onKeydownMenuButton(state, e),
        refContainerCb: (el) => {
            if (overlayElement) {
                el.style.zIndex = "1000";
            }
            if (props.ref) {
                // @ts-ignore
                props.ref(el);
            }
            state.containerEl = el;
        },
        refOverlayCb: (el) => {
            el.style.position = "fixed";
            el.style.top = "0";
            el.style.left = "0";
            el.style.width = "100%";
            el.style.height = "100%";
            el.style.zIndex = "1000";
            if (typeof overlayElement === "object" && overlayElement.ref) {
                overlayElement.ref(el);
            }
            state.overlayEl = el;
        },
    };
    let marker = mount ? document.createTextNode("") : null;
    const initDefer = !props.open();
    let containerEl;
    let mountEl;
    let endExitTransitionRef;
    let endEnterTransitionRef;
    let endExitTransitionOverlayRef;
    let endEnterTransitionOverlayRef;
    let exitRunning = false;
    function getElement(el, appendToElement) {
        if (appendToElement) {
            if (appendToElement === "menuPopup") {
                return queryElement({ containerEl: el }, { inputElement: null, type: "menuPopup" });
            }
            return typeof appendToElement === "string"
                ? el.querySelector(appendToElement)
                : appendToElement;
        }
        return el;
    }
    function enterTransition(type, el) {
        // @ts-ignore
        if (type === "overlay" && (!props.overlay || !props.overlay.animation))
            return;
        const animation = type === "popup"
            ? props.animation // @ts-ignore
            : props.overlay.animation;
        if (!animation)
            return;
        if (!animation.appear && !initDefer)
            return;
        exitRunning = false;
        el = getElement(el, animation.appendToElement);
        const name = animation.name;
        let { onBeforeEnter, onEnter, onAfterEnter, enterActiveClass = name + "-enter-active", enterClass = name + "-enter", enterToClass = name + "-enter-to", exitActiveClass = name + "-exit-active", exitClass = name + "-exit", exitToClass = name + "-exit-to", } = animation;
        const enterClasses = enterClass.split(" ");
        const enterActiveClasses = enterActiveClass.split(" ");
        const enterToClasses = enterToClass.split(" ");
        const exitClasses = exitClass.split(" ");
        const exitActiveClasses = exitActiveClass.split(" ");
        const exitToClasses = exitToClass.split(" ");
        if (type === "popup") {
            el.removeEventListener("transitionend", endExitTransitionRef);
            el.removeEventListener("animationend", endExitTransitionRef);
        }
        else {
            el.removeEventListener("transitionend", endExitTransitionOverlayRef);
            el.removeEventListener("animationend", endExitTransitionOverlayRef);
        }
        onBeforeEnter && onBeforeEnter(el);
        el.classList.remove(...exitClasses, ...exitActiveClasses, ...exitToClasses);
        el.classList.add(...enterClasses);
        el.classList.add(...enterActiveClasses);
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                el.classList.remove(...enterClasses);
                el.classList.add(...enterToClasses);
                onEnter && onEnter(el, endTransition);
                if (!onEnter || onEnter.length < 2) {
                    if (type === "popup") {
                        endEnterTransitionRef = endTransition;
                    }
                    else {
                        endEnterTransitionOverlayRef = endTransition;
                    }
                    el.addEventListener("transitionend", endTransition, {
                        once: true,
                    });
                    el.addEventListener("animationend", endTransition, {
                        once: true,
                    });
                }
            });
        });
        function endTransition() {
            if (el) {
                el.classList.remove(...enterActiveClasses);
                el.classList.remove(...enterToClasses);
                onAfterEnter && onAfterEnter(el);
            }
        }
    }
    function exitTransition(type, el) {
        if (!props.animation) {
            mountEl?.removeChild(containerEl);
            containerEl = null;
            mountEl = null;
            return;
        }
        // @ts-ignore
        if (type === "overlay" && (!props.overlay || !props.overlay.animation))
            return;
        // @ts-ignore
        const animation = type === "popup"
            ? props.animation
            : // @ts-ignore
                props.overlay.animation;
        exitRunning = true;
        el = getElement(el, animation.appendToElement);
        const name = animation.name;
        let { onBeforeExit, onExit, onAfterExit, exitActiveClass = name + "-exit-active", exitClass = name + "-exit", exitToClass = name + "-exit-to", } = animation;
        const exitClasses = exitClass.split(" ");
        const exitActiveClasses = exitActiveClass.split(" ");
        const exitToClasses = exitToClass.split(" ");
        if (type === "popup") {
            el.removeEventListener("transitionend", endEnterTransitionRef);
            el.removeEventListener("animationend", endEnterTransitionRef);
        }
        else {
            el.removeEventListener("transitionend", endEnterTransitionOverlayRef);
            el.removeEventListener("animationend", endEnterTransitionOverlayRef);
        }
        if (!el.parentNode)
            return endTransition();
        onBeforeExit && onBeforeExit(el);
        el.classList.add(...exitClasses);
        el.classList.add(...exitActiveClasses);
        requestAnimationFrame(() => {
            el.classList.remove(...exitClasses);
            el.classList.add(...exitToClasses);
        });
        onExit && onExit(el, endTransition);
        if (!onExit || onExit.length < 2) {
            if (type === "popup") {
                endExitTransitionRef = endTransition;
            }
            else {
                endExitTransitionOverlayRef = endTransition;
            }
            el.addEventListener("transitionend", endTransition, { once: true });
            el.addEventListener("animationend", endTransition, { once: true });
        }
        function endTransition() {
            exitRunning = false;
            mountEl?.removeChild(containerEl);
            globalState.closedBySetOpen = false;
            if (state.menuBtnEls && (overlay || overlayElement)) {
                if (document.activeElement === document.body) {
                    const menuBtnEl = getMenuButton(state.menuBtnEls);
                    menuBtnEl.focus();
                }
            }
            onAfterExit && onAfterExit(el);
            containerEl = null;
            mountEl = null;
        }
    }
    const runRemoveScrollbar = (open) => {
        if (!removeScrollbar)
            return;
        if (dismissStack.length > 1)
            return;
        if (open) {
            const el = document.scrollingElement;
            el.style.overflow = "hidden";
        }
        else {
            const el = document.scrollingElement;
            el.style.overflow = "";
        }
    };
    const resetFocusOnClose = () => {
        const activeElement = document.activeElement;
        if (activeElement !== document.body) {
            if (state.menuBtnEls.every((menuBtnEl) => activeElement !== menuBtnEl) &&
                !state.containerEl?.contains(activeElement)) {
                return;
            }
        }
        const { menuBtnEls, focusedMenuBtn, timeouts } = state;
        const menuBtnEl = getMenuButton(menuBtnEls);
        const el = queryElement(state, {
            inputElement: focusElementOnClose,
            type: "focusElementOnClose",
            subType: "click",
        }) || menuBtnEl;
        if (el) {
            el.focus();
            if (el === menuBtnEl) {
                markFocusedMenuButton({ focusedMenuBtn, timeouts, el });
            }
        }
    };
    const programmaticRemoval = () => {
        if (globalState.closedByEvents)
            return;
        const activeElement = document.activeElement;
        if (
        // activeElement !== state.menuBtnEls
        state.menuBtnEls.every((menuBtnEl) => activeElement !== menuBtnEl) &&
            !state.containerEl?.contains(activeElement)) {
            setTimeout(() => {
                globalState.closedBySetOpen = false;
            });
            return;
        }
        if (!globalState.closedBySetOpen) {
            globalState.addedDocumentClick = false;
            document.removeEventListener("click", onDocumentClick);
            globalState.closedBySetOpen = true;
            setTimeout(() => {
                globalState.closedBySetOpen = false;
                resetFocusOnClose();
            });
        }
    };
    createEffect(on(() => typeof props.menuButton === "function"
        ? props.menuButton()
        : props.menuButton, (menuButton) => {
        if (Array.isArray(menuButton) && !menuButton.length)
            return;
        const { focusedMenuBtn } = state;
        const menuBtnEls = queryElement(state, {
            inputElement: menuButton,
            type: "menuButton",
        });
        if (!menuBtnEls)
            return;
        state.menuBtnEls = Array.isArray(menuBtnEls)
            ? menuBtnEls
            : [menuBtnEls];
        state.menuBtnEls.forEach((menuBtnEl, _, self) => {
            if (focusedMenuBtn.el &&
                focusedMenuBtn.el !== menuBtnEl &&
                (self.length > 1 ? !hasDisplayNone(menuBtnEl) : true)) {
                focusedMenuBtn.el = menuBtnEl;
                menuBtnEl.focus({ preventScroll: true });
                menuBtnEl.addEventListener("keydown", state.onKeydownMenuButtonRef);
            }
            menuBtnEl.setAttribute("type", "button");
            menuBtnEl.addEventListener("click", state.onClickMenuButtonRef);
            menuBtnEl.addEventListener("mousedown", state.onMouseDownMenuButtonRef);
            if (props.open() &&
                (!state.focusElementOnOpen ||
                    state.focusElementOnOpen === "menuButton" ||
                    state.focusElementOnOpen === state.menuBtnEls) &&
                !hasDisplayNone(menuBtnEl)) {
                menuBtnEl.addEventListener("blur", state.onBlurMenuButtonRef, {
                    once: true,
                });
            }
        });
        const item = dismissStack.find((item) => item.uniqueId === state.uniqueId);
        if (item) {
            item.menuBtnEls = state.menuBtnEls;
        }
        onCleanup(() => {
            if (!state)
                return;
            removeMenuButtonEvents(state, true);
        });
    }));
    if (show && mount) {
        CreatePortal({
            mount: typeof mount === "string" ? document.querySelector(mount) : mount,
            popupChildren: render(props.children),
            overlayChildren: overlayElement ? renderOverlay() : null,
            marker,
            onCreate: (mount, container) => {
                mountEl = mount;
                containerEl = container;
            },
        });
    }
    createComputed(on(() => !!props.open(), (open, prevOpen) => {
        if (open === prevOpen)
            return;
        if (!open) {
            if (state.focusSentinelAfterEl) {
                state.focusSentinelAfterEl.tabIndex = -1;
            }
            programmaticRemoval();
        }
        if (!mount || show)
            return;
        if (open) {
            if (!mountEl) {
                CreatePortal({
                    mount: typeof mount === "string"
                        ? document.querySelector(mount)
                        : mount,
                    popupChildren: render(props.children),
                    overlayChildren: overlayElement ? renderOverlay() : null,
                    marker,
                    onCreate: (mount, container) => {
                        mountEl = mount;
                        containerEl = container;
                    },
                });
            }
            enterTransition("popup", containerEl?.firstElementChild);
            enterTransition("overlay", state.overlayEl);
        }
        else {
            exitTransition("popup", containerEl?.firstElementChild);
            exitTransition("overlay", state.overlayEl);
        }
    }, { defer: initDefer }));
    createEffect(on(() => !!props.open(), (open, prevOpen) => {
        if (open === prevOpen)
            return;
        if (open) {
            globalState.closedByEvents = false;
            addMenuPopupEl(state);
            runFocusOnActive(state);
            addGlobalEvents(closeWhenScrolling);
            addDismissStack({
                id,
                uniqueId: state.uniqueId,
                open: props.open,
                setOpen: props.setOpen,
                containerEl: state.containerEl,
                menuBtnEls: state.menuBtnEls,
                focusedMenuBtn: state.focusedMenuBtn,
                overlayEl: state.overlayEl,
                menuPopupEl: state.menuPopupEl,
                overlay,
                closeWhenDocumentBlurs,
                closeWhenEscapeKeyIsPressed,
                closeWhenMenuButtonIsTabbed,
                overlayElement,
                cursorKeys,
                focusElementOnClose,
                focusSentinelBeforeEl: state.focusSentinelBeforeEl,
                upperStackRemovedByFocusOut: false,
                detectIfMenuButtonObscured: false,
                queueRemoval: false,
                timeouts: state.timeouts,
            });
            runRemoveScrollbar(open);
            onOpen && onOpen(open, { uniqueId: state.uniqueId, dismissStack });
            activateLastFocusSentinel(state);
        }
        else {
            globalState.closedByEvents = false;
            removeLocalEvents(state);
            removeOutsideFocusEvents(state);
            removeMenuPopupEl(state);
            removeDismissStack(state.uniqueId);
            removeGlobalEvents();
            runRemoveScrollbar(open);
            onOpen && onOpen(open, { uniqueId: state.uniqueId, dismissStack });
        }
    }, { defer: initDefer }));
    onCleanup(() => {
        removeLocalEvents(state, { onCleanup: true });
        removeMenuPopupEl(state);
        removeOutsideFocusEvents(state);
        removeDismissStack(state.uniqueId);
        removeGlobalEvents();
        if (!show && mount && mountEl && !exitRunning) {
            exitTransition("popup", containerEl?.firstElementChild);
            exitTransition("overlay", state.overlayEl);
        }
    });
    function renderOverlay() {
        return (<div class={typeof props.overlayElement === "object"
                ? props.overlayElement.class
                : undefined} classList={typeof props.overlayElement === "object"
                ? props.overlayElement.classList || {}
                : {}} role="presentation" onClick={state.onClickOverlayRef} ref={state.refOverlayCb}></div>);
    }
    function render(children) {
        return (<div id={state.id} class={props.class} classList={props.classList || {}} onFocusIn={state.onFocusInContainerRef} onFocusOut={state.onFocusOutContainerRef} ref={state.refContainerCb}>
        <div tabindex={props.open() ? "0" : "-1"} onFocus={(e) => {
                onFocusSentinel(state, "before", e.relatedTarget);
            }} style="position: fixed; top: 0; left: 0; outline: none; pointer-events: none; width: 0; height: 0;" aria-hidden="true" ref={state.focusSentinelBeforeEl}></div>
        {children}
        <div tabindex={props.open() && state.hasFocusSentinels ? "0" : "-1"} onFocus={() => {
                onFocusSentinel(state, "after");
            }} style="position: fixed; top: 0; left: 0; outline: none; pointer-events: none; width: 0; height: 0;" aria-hidden="true" ref={state.focusSentinelAfterEl}></div>
      </div>);
    }
    if (props.mount)
        return marker;
    if (show)
        return render(props.children);
    let strictEqual = false;
    const condition = createMemo(() => props.open(), undefined, {
        equals: (a, b) => (strictEqual ? a === b : !a === !b),
    });
    const finalRender = createMemo(() => {
        const c = condition();
        if (c) {
            const child = props.children;
            return (strictEqual = typeof child === "function" && child.length > 0)
                ? untrack(() => child(c))
                : render(child);
        }
    });
    if (props.animation) {
        return (<Transition {...props.animation} name={props.animation.name} enterClass={props.animation.enterClass} enterActiveClass={props.animation.enterActiveClass} enterToClass={props.animation.enterToClass} exitClass={props.animation.exitClass} exitActiveClass={props.animation.exitActiveClass} exitToClass={props.animation.exitToClass} appear={props.animation.appear}>
        {finalRender()}
      </Transition>);
    }
    return finalRender;
};
export default Dismiss;
